// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// Conflict Check tool — under Tools → Conflict Check.
//
// Required workflow:
//   1. Pick a client (the prospective client this check is being run for —
//      yes, even at intake, the client must already exist in the portal).
//   2. Fill out the primary party form (name required, email/phone optional).
//   3. Optionally add related parties — opposing party, spouse, business
//      partners, witnesses, etc. — each searched the same way as primary.
//   4. Run the check. Results show on screen and a row is recorded in the
//      conflict_checks table so the client's audit trail picks it up.
//
// The disclaimer is ALWAYS visible. It appears at the top of the page,
// inside the results panel, and on the prior-checks panel. This is the
// legal CYA; it must not be hidden or made dismissible.

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';
import ClientPicker from '../components/ClientPicker';
import ConflictChecksPanel, { ConflictCheckDisclaimer, MatchesList } from '../components/ConflictChecksPanel';

const emptyParty = () => ({ name: '', email: '', phone: '', relation: '' });

export default function AdminConflictCheckPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const preselectedClientId = searchParams.get('clientId') || '';

  const [clientId, setClientId] = useState(preselectedClientId);
  const [selectedClient, setSelectedClient] = useState(null);

  const [primary, setPrimary] = useState({ name: '', email: '', phone: '' });
  const [related, setRelated] = useState([]);

  const [running, setRunning] = useState(false);
  const [error, setError]     = useState('');
  const [result, setResult]   = useState(null);   // {matches, parties, summary, savedCheckId}
  const [refreshKey, setRefreshKey] = useState(0); // bump after save to reload the prior-checks list

  // Fetch the single selected client (only) so we can show "saved on X's
  // file" with a link. The full client list is loaded inside ClientPicker.
  useEffect(() => {
    if (!clientId) { setSelectedClient(null); return; }
    let cancelled = false;
    adminApi.getClient(clientId)
      .then(c => { if (!cancelled) setSelectedClient(c); })
      .catch(() => { if (!cancelled) setSelectedClient(null); });
    return () => { cancelled = true; };
  }, [clientId]);

  // Keep clientId in the URL so refreshing the page doesn't lose your pick.
  // searchParams/setSearchParams are included for hook-deps hygiene; the
  // effect is idempotent on re-runs with the same clientId (setSearchParams
  // with the same value is a no-op), so this can't loop.
  useEffect(() => {
    if (clientId) {
      const next = new URLSearchParams(searchParams);
      if (next.get('clientId') !== clientId) {
        next.set('clientId', clientId);
        setSearchParams(next, { replace: true });
      }
    }
  }, [clientId, searchParams, setSearchParams]);

  function updatePrimary(k, v) { setPrimary(p => ({ ...p, [k]: v })); }
  function addRelated()        { setRelated(r => [...r, emptyParty()]); }
  function updateRelated(i, k, v) {
    setRelated(r => r.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  }
  function removeRelated(i)    { setRelated(r => r.filter((_, idx) => idx !== i)); }

  async function handleRun() {
    setError('');
    setResult(null);
    if (!clientId) { setError('Please select a client first.'); return; }
    if (!primary.name.trim()) { setError('Primary party name is required.'); return; }

    setRunning(true);
    try {
      const r = await adminApi.runConflictCheck({
        clientId,
        primary,
        related: related.filter(p => p.name.trim()),
      });
      setResult(r);
      setRefreshKey(k => k + 1); // refresh prior checks
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  }

  function handleReset() {
    setPrimary({ name: '', email: '', phone: '' });
    setRelated([]);
    setResult(null);
    setError('');
  }

  return (
    <div>
      <h1 className="page-title">Conflict Check</h1>
      <p className="page-subtitle">
        Search clients and contacts for potential conflicts before taking on a new matter.
        Every check is recorded on the linked client&apos;s file as an audit trail.
      </p>

      <ConflictCheckDisclaimer />

      {error && <div className="alert alert-error">{error}</div>}

      {/* ── Client selection + party form ────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Run a new check</div>

        <div className="form-group">
          <label>Link this check to a client <span style={{ color: '#B85042' }}>*</span></label>
          <ClientPicker
            value={clientId}
            onChange={setClientId}
            statusFilter="all"
            disabled={running}
            placeholder="Search by name, email, or matter number…"
          />
          {clientId && selectedClient && (
            <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
              The check will be saved on {selectedClient.name}&apos;s file.{' '}
              <a href={`/admin/clients/${clientId}`} target="_blank" rel="noreferrer" style={{ color: '#2f5496' }}>
                Open client →
              </a>
            </div>
          )}
        </div>

        <PartyForm
          title="Primary party (prospective client)"
          required
          party={primary}
          onChange={updatePrimary}
        />

        {related.map((p, i) => (
          <RelatedPartyForm
            key={i}
            party={p}
            onChange={(k, v) => updateRelated(i, k, v)}
            onRemove={() => removeRelated(i)}
          />
        ))}

        <div className="btn-row" style={{ marginTop: 12 }}>
          <button className="btn" onClick={addRelated} disabled={running}>
            + Add related party
          </button>
          {(primary.name || related.length > 0) && (
            <button className="btn" onClick={handleReset} disabled={running}>Clear form</button>
          )}
          <button
            className="btn btn-primary"
            onClick={handleRun}
            disabled={running || !clientId || !primary.name.trim()}
          >
            {running ? 'Running...' : 'Run Conflict Check'}
          </button>
        </div>
      </div>

      {/* ── Results ──────────────────────────────────────────────────────── */}
      {result && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title">
            Results
            <span style={{ fontWeight: 400, color: '#666', fontSize: 13, marginLeft: 8 }}>
              — {result.matches.length} match{result.matches.length === 1 ? '' : 'es'} found
            </span>
          </div>
          <ConflictCheckDisclaimer compact />
          <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
            Saved as audit-trail entry at {new Date(result.runAt).toLocaleString()}.
          </div>
          <MatchesList matches={result.matches} />
        </div>
      )}

      {/* ── Prior checks for the selected client ────────────────────────── */}
      {clientId && (
        <div className="card">
          <ConflictChecksPanel clientId={clientId} refreshKey={refreshKey} />
        </div>
      )}
    </div>
  );
}

function PartyForm({ title, required, party, onChange }) {
  return (
    <div style={{
      border: '1px solid #e8e8e8',
      borderRadius: 6,
      padding: 16,
      marginTop: 12,
      background: '#fafafa',
    }}>
      <div style={{ fontWeight: 600, fontFamily: 'Arial, sans-serif', fontSize: 14, marginBottom: 10 }}>
        {title}
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Full name {required && <span style={{ color: '#B85042' }}>*</span>}</label>
          <input
            type="text"
            value={party.name}
            onChange={e => onChange('name', e.target.value)}
            placeholder="e.g. John Q. Smith"
          />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Email <span style={{ fontWeight: 400, color: '#888' }}>(optional)</span></label>
          <input
            type="email"
            value={party.email}
            onChange={e => onChange('email', e.target.value)}
            placeholder="name@example.com"
          />
        </div>
        <div className="form-group">
          <label>Phone <span style={{ fontWeight: 400, color: '#888' }}>(optional)</span></label>
          <input
            type="tel"
            value={party.phone}
            onChange={e => onChange('phone', e.target.value)}
            placeholder="(555) 123-4567"
          />
        </div>
      </div>
    </div>
  );
}

function RelatedPartyForm({ party, onChange, onRemove }) {
  return (
    <div style={{
      border: '1px solid #e8e8e8',
      borderRadius: 6,
      padding: 16,
      marginTop: 12,
      background: '#fafafa',
      position: 'relative',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontWeight: 600, fontFamily: 'Arial, sans-serif', fontSize: 14 }}>
          Related party
        </div>
        <button className="btn btn-sm" onClick={onRemove} title="Remove this party">Remove</button>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Relationship</label>
          <select value={party.relation} onChange={e => onChange('relation', e.target.value)}>
            <option value="">— Select —</option>
            <option value="Opposing party">Opposing party</option>
            <option value="Spouse">Spouse</option>
            <option value="Ex-spouse">Ex-spouse</option>
            <option value="Child">Child</option>
            <option value="Parent">Parent</option>
            <option value="Sibling">Sibling</option>
            <option value="Business partner">Business partner</option>
            <option value="Employer">Employer</option>
            <option value="Witness">Witness</option>
            <option value="Attorney for opposing party">Attorney for opposing party</option>
            <option value="Co-counsel">Co-counsel</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 2 }}>
          <label>Full name</label>
          <input
            type="text"
            value={party.name}
            onChange={e => onChange('name', e.target.value)}
            placeholder="e.g. Jane Doe"
          />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Email <span style={{ fontWeight: 400, color: '#888' }}>(optional)</span></label>
          <input
            type="email"
            value={party.email}
            onChange={e => onChange('email', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Phone <span style={{ fontWeight: 400, color: '#888' }}>(optional)</span></label>
          <input
            type="tel"
            value={party.phone}
            onChange={e => onChange('phone', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}
